import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { useWorkflowVersionMutations } from 'src/graphql/entities/workflow';
import {
  CreateDraftWorkflowVersionField,
  type CreateDraftWorkflowVersionFormValues,
} from 'src/pages/Settings/workflows/components/create-draft-version-form';
import { CreateDraftWorkflowVersionFields } from 'src/pages/Settings/workflows/components/create-draft-version-form-fields';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';

const DEFAULT_WORKFLOW_DEFINITION = '{\n  "nodes": [],\n  "edges": []\n}';

export const CreateDraftWorkflowVersionModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showSuccessAlert } = useNotifications();
  const { closeModal } = useModalMethods(Modals.CreateDraftWorkflowVersion);
  const { isOpened, modalProps } = useModalState(Modals.CreateDraftWorkflowVersion);
  const { createDraftWorkflowVersion, loading } = useWorkflowVersionMutations({
    partnerId: selectedPartnerId,
    refetchWorkflowVersions: modalProps?.refetchWorkflowVersions,
  });

  const workflows = modalProps?.workflows ?? [];
  const initialDefinition = modalProps?.initialDefinition;
  const isSubmitting = loading.isCreatingDraft;

  const defaultValues = useMemo<CreateDraftWorkflowVersionFormValues>(
    () => {
      const parsedInitialDefinition = initialDefinition
        ? parseJsonSafely<Record<string, unknown>>(initialDefinition)
        : null;

      return {
        [CreateDraftWorkflowVersionField.WorkflowId]: modalProps?.workflowId ?? '',
        [CreateDraftWorkflowVersionField.Description]: '',
        [CreateDraftWorkflowVersionField.Definition]: parsedInitialDefinition
          ? JSON.stringify(parsedInitialDefinition, null, 2)
          : initialDefinition?.trim() || DEFAULT_WORKFLOW_DEFINITION,
      };
    },
    [initialDefinition, modalProps?.workflowId],
  );

  const formMethods = useForm<CreateDraftWorkflowVersionFormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods, isOpened]);

  const handleClose = useCallback(() => {
    formMethods.reset(defaultValues);
    closeModal();
  }, [closeModal, defaultValues, formMethods]);

  const onSubmit = useCallback(
    async (values: CreateDraftWorkflowVersionFormValues) => {
      const result = await createDraftWorkflowVersion({
        workflowId: values[CreateDraftWorkflowVersionField.WorkflowId],
        definition: values[CreateDraftWorkflowVersionField.Definition],
        description: values[CreateDraftWorkflowVersionField.Description].trim() || undefined,
      });

      if (!result) {
        return;
      }

      showSuccessAlert(`Draft workflow version created successfully (v${result.version})`);
      modalProps?.onCreated?.(result);
      handleClose();
    },
    [createDraftWorkflowVersion, handleClose, modalProps, showSuccessAlert],
  );

  return (
    <Dialog
      open={isOpened}
      onClose={isSubmitting ? undefined : handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: 'min(80vh, 47.5rem)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
          px: 3,
        }}
      >
        Create Draft Workflow Version
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          component="form"
          onSubmit={formMethods.handleSubmit(onSubmit)}
          noValidate
          sx={{ minHeight: 0, flex: 1 }}
        >
          <GenericForm<CreateDraftWorkflowVersionFormValues>
            formMethods={formMethods}
            gap={3}
            sx={{ minHeight: 0, flex: 1 }}
          >
            <CreateDraftWorkflowVersionFields disabled={isSubmitting} workflows={workflows} />

            <DialogActions sx={{ px: 0, pb: 0 }}>
              <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || workflows.length === 0}
              >
                {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Create'}
              </Button>
            </DialogActions>
          </GenericForm>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
